import React, { useState, Fragment } from "react";
import {
	useTable,
	useFlexLayout,
	useExpanded,
	useResizeColumns,
} from "react-table";
import { formatValue } from "./Utils.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
// import { Tooltip } from 'react-tooltip';
import Tooltip from "./form-components/Tooltip.jsx";

import "./ResourceTable.css";


function Table({ columns, data, setHoveredRow, expandAll }) {
	const openResourceTypes = sessionStorage.getItem('openResourceTypes');
	const openRows = openResourceTypes ? openResourceTypes.split(",") : [];
	let defaultExpandedRows = {};

	data.forEach((element, index) => {
		if (openRows.includes(element.resource_type) || expandAll) {
			defaultExpandedRows[index] = true;
		}
	});

	const defaultColumn = React.useMemo(
		() => ({
		  minWidth: 30,
		  width: 150,
		  maxWidth: 800,
		}),
		[]
	  )

	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		useTable(
			{
				columns,
				data,
				defaultColumn,
				autoResetExpanded: false,
				collapseOnDataChange: false,
				initialState: { expanded: defaultExpandedRows },
			},
			useFlexLayout,
			useExpanded,
			useResizeColumns
		);


	return (
		<div
			{...getTableProps()}
			className="flex w-full cursor-default flex-auto flex-col bg-card mr-6"
		>
			<div className="py-2 border-b border-border pl-2 text-text-2 sticky top-0 z-50 bg-card">
				{headerGroups.map((headerGroup) => (
					<div {...headerGroup.getHeaderGroupProps()} className="tr">
						{headerGroup.headers.map((column) => (
							<div
								{...column.getHeaderProps()}
								className="th uppercase text-sm"
							>
								{column.render("Header")}
								<div
									{...column.getResizerProps()}
									className={`resizer ${column.isResizing ? "isResizing" : ""}`}
								/>
							</div>
						))}
					</div>
				))}
			</div>

			<div {...getTableBodyProps()} className="">	
				{rows.map((row, index) => {
					prepareRow(row);

					return (
						<div
							{...row.getRowProps({
							})}
							className={
								"tr border-b border-border hover:bg-surface-2 h-[77px] cursor-pointer pl-4" +
								(index === 0 ? " rounded-t-[16px]" : "") +
								(index === rows.length - 1 ? " rounded-b-[16px] border-b-0" : "")
							}						
						>
							{row.cells.map((cell) => {
								return (
									<div
										{...cell.getCellProps()}
										className="td truncate pl-1 pt-6 pb-4 text-sm"
									>
										{cell.render("Cell")}
									</div>
								);
							})}
						</div>
					);
				})}
			</div>
		</div>
	);
}

const ResourceChangesTable = ({ data, grouping, toggleRow, openRows, expandAll }) => {
	const [, setHoveredRow] = useState(-1);
	const navigate = useNavigate();

	const openDetails = (resourceID, type) => {
		if (type === "Resource") {
			navigate("/resource/" + resourceID);
		}
	}

	const columnDefs = [
		{
			accessor: "change_date",
			id: "change_date",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="The date of the change">
						Change date
				</span>
			),
			Cell: (cell) => {
				return (
					<div className="text-left text-text pt-1 pl-2">
						{dayjs(cell.value).format("dddd, MMMM D")}
					</div>
				);
			},
			width: 100,
		},
		{
			accessor: "vendor_name",
			// Header: (row) => <div className="">Vendor</div>,
			Cell: (cell) => {
                return (
                    <div className="text-center text-text font-semibold pt-1">
                        <FontAwesomeIcon icon={faGoogle} className="text-text-2" />
                    </div>
                );
			},
			minWidth: 50,
			width: 50,
			maxWidth: 50,
		},
		{
			accessor: "resource_cluster",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="The resource cluster">
					Cluster
				</span>
			),
			Cell: (cell) => {
				if (cell.row.original.resource_name) {
					return (
						<div className="text-left text-text pt-1">
							{cell.value}
						</div>
					);
				}
			},
			width: 100,
		},
		{
			accessor: "min_change",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="Min change">
					Min change
				</span>
			),
			Cell: (cell) => {
				if (cell.row.original.resource_name) {
					return (
						<div className="text-right text-text pt-1 mr-8">
							{cell.value !== 0 ? formatValue(cell.value, "decimal", true) : null}
						</div>
					);
				}
			},
			width: 50,
		},
		{
			accessor: "plus_change",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="Plus change">
					Plus change
				</span>
			),
			Cell: (cell) => {
				if (cell.row.original.resource_name) {
					return (
						<div className="text-right text-text pt-1 mr-8">
							{cell.value !== 0 ? formatValue(cell.value, "decimal", true) : null}
						</div>
					);
				}
			},
			width: 50,
		},
		{
			accessor: "resource_type",
			Header: () => (
				<span className="ml-16" data-tooltip-id="instantTooltip" data-tooltip-content="The type of the resource">
					Type
				</span>
			),
			Cell: (cell) => {
                return (
                    <div className="text-left text-text pt-1 ml-16">
                        {cell.value}
                    </div>
                );
			},
			width: 200,
		},
		{
			accessor: "resource_name",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="The name of the resource">
					Resource
				</span>
			),
			Cell: (cell) => {
				return (
					<div 
						className="text-left text-text pt-1 cursor-pointer"
						onClick={(e) => { 
							openDetails(cell.row.original.resource_md5, cell.row.original.type)
						} }
					>
						{cell.value}
					</div>
				);	
			},
			width: 250,
            maxWidth: 250,
            minWidth: 250,
		},
		{
			accessor: "resource_status",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="Is the resource currently active or inactive">
					Status
				</span>
			),
			Cell: (cell) => {
                return (
                    <div className="text-left pt-1">
                        <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold
                                ${cell.value && cell.value.toLowerCase() === "active"
                                    ? "bg-success-soft text-success border border-success"
                                    : cell.value && cell.value.toLowerCase() === "inactive"
                                        ? "bg-danger-soft text-danger border border-danger"
                                        : "bg-surface-2 text-text-2 border border-border"
                                }`
                            }
                        >
                            {cell.value}
                        </span>
                    </div>
                );
			},
			width: 100,
            maxWidth: 100,
            minWidth: 100,
		},
		{
			accessor: "cost",
			Header: (row) => (
				<div className="text-right mr-8">
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The cost of the resource during the selected time period">
						Cost
					</span>
				</div>
			),
			Cell: ({row}) => {
				let valueClass = "text-right text-text pt-1 mr-8";
				if (row.original.subRows && row.original.subRows.length > 0) valueClass += "font-semibold";

				if (row.original.costs_not_known) {
					return (
						<div className={valueClass}>
							
						</div>
					)
				} else {
					return (
						<div className={valueClass}>
							{formatValue(row.original.cost, "currency", false)}
						</div>
					)
				}
			},
			width: 100,
			maxWidth: 100,
			minWidth: 100,
		},

	];

	if (data && data.length > 0) {
		return (
			<Fragment>
				<div className="bg-card p-4 my-2 rounded-card border border-card-bd mr-6">
					<Tooltip />
					<Table
						className="flex-auto ml-12"
						columns={columnDefs}
						data={data}
						renderRowSubComponent
						expandedRows={false}
						openRows={openRows}
						setHoveredRow={setHoveredRow}
						expandAll={expandAll}
					/>
				</div>
			</Fragment>
		);
	} else {
		return (
			<Fragment>
				<div className="ml-10 font-semibold text-3xl text-text"></div>
			</Fragment>
		);
	}
};

export default ResourceChangesTable;
